import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import classnames from 'classnames';
import CustomForm from '../../../../core/components/core/Util/Form/CustomForm/CustomForm';
import config from '../../../config/config';
import Fetch from "../../../../core/util/core/Fetch";
import Toast from "../../../../core/components/core/Util/Toast/Toast";
import { chooseProfileFilter } from "../../../helpers/chooseProfileFilter";
import { useBasePath } from "../../../../core/util/core/Path";
import sleep from "sleep-promise";
import { v4 as uuidv4 } from "uuid";

function Form() {
    const { id } = useParams();
    const navigate = useNavigate();
    const basePath = useBasePath();
    const profileFilter = chooseProfileFilter();

    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [values, setValues] = useState({});
    const [roleNameOptions, setRoleNameOptions] = useState([]);
    const [formContent, setFormContent] = useState(<></>);

    const hiddenValues = {};
    if (profileFilter.pdc_entity_id) {
        hiddenValues.pdc_entity_id = profileFilter.pdc_entity_id;
    }
    if (profileFilter.pdc_company_id) {
        hiddenValues.pdc_company_id = profileFilter.pdc_company_id;
    }

    const path = '/positions/';
    const paths = {
        endpointSave: config.endpoints.backend + 'positions/save',
        endpointGetById: config.endpoints.backend + 'positions/getById',
        endpointGetAllCompanies: config.endpoints.backend + 'companies/getall',
        endpointGetAllEntities: config.endpoints.backend + 'entities/getall',
        endpointGetAllRoleNames: config.endpoints.backend + 'rolenames/getall',
        endpointGetAllCboSynonyms: config.endpoints.backend + 'cbosynonyms/getall',
        endpointGetAllRoleClasses: config.endpoints.backend + 'roleclasses/getall',
        endpointGetAllRoleClassifications: config.endpoints.backend + 'roleclassifications/getall',
        endpointGetAllSectors: config.endpoints.backend + 'sectors/getall',
        endpointGetAllLeadershipPositions: config.endpoints.backend + 'leadershippositions/getall',
        endpointGetAllEducationLevels: config.endpoints.backend + 'educationlevels/getall',
        endpointGetAllTechnicalCompetencies: config.endpoints.backend + 'technicalcompetencies/getall',
        endpointGetAllBehavioralCompetencies: config.endpoints.backend + 'behavioralcompetencies/getall',
        endpointGetAllTrainings: config.endpoints.backend + 'trainings/getall',
        endpointGetAllSystemAccesses: config.endpoints.backend + 'systemaccesses/getall',
        urlGoBack: path + 'list',
        pathView: path + 'view'
    };

    const isViewMode = basePath === paths.pathView;

    const loadRoleNameOptions = async (entityId) => {
        if (!entityId) {
            setRoleNameOptions([]);
            return [];
        }
        const rows = await Fetch.post(paths.endpointGetAllRoleNames, { filter: { pdc_entity_id: entityId } });
        setRoleNameOptions(rows);
        return rows;
    };

    const syncValues = (element, field, formValues) => {
        setValues(prev => ({ ...prev, ...formValues }));
    };

    const getGeneralFields = (currentValues, roleNameOptionsList) => {
        const entityId = !!currentValues.pdc_entity_id ? currentValues.pdc_entity_id : (!!profileFilter.pdc_entity_id ? profileFilter.pdc_entity_id : null);
        const currentRoleNameOptions = roleNameOptionsList !== undefined ? roleNameOptionsList : roleNameOptions;

        return [
            !hiddenValues.pdc_company_id && !hiddenValues.pdc_entity_id && !profileFilter.pdc_project_ids ? {
                name: 'pdc_company_id',
                type: 'select',
                label: 'Empresa',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllCompanies,
                    label: 'corporate_name',
                    value: 'id',
                    params: { filter: { ...profileFilter } }
                },
                handleChange: async (element, field, formValues) => {
                    formValues.pdc_entity_id = '';
                    formValues.pdc_role_name_id = '';
                    formValues.cbo_display = '';
                    formValues.pdc_cbo_role_class_id = '';
                    formValues.pdc_cbo_role_classification_id = '';
                    formValues.pdc_cbo_sector_id = '';
                    formValues.pdc_cbo_leadership_position_id = '';
                    formValues.pdc_cbo_education_level_id = '';
                    const freshRoleNameOptions = await loadRoleNameOptions('');
                    setValues(prev => ({ ...prev, ...formValues }));
                    await renderTab('general', formValues, freshRoleNameOptions);
                },
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            } : {},
            !hiddenValues.pdc_entity_id && !profileFilter.pdc_project_ids ? {
                name: 'pdc_entity_id',
                type: 'select',
                label: 'Entidade',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllEntities,
                    label: 'corporate_name',
                    value: 'id',
                    params: {
                        filter: {
                            ...profileFilter,
                            pdc_company_id: !!currentValues.pdc_company_id ? currentValues.pdc_company_id : (!!profileFilter.pdc_company_id ? profileFilter.pdc_company_id : null)
                        }
                    }
                },
                handleChange: async (element, field, formValues) => {
                    formValues.pdc_role_name_id = '';
                    formValues.cbo_display = '';
                    formValues.pdc_cbo_role_class_id = '';
                    formValues.pdc_cbo_role_classification_id = '';
                    formValues.pdc_cbo_sector_id = '';
                    formValues.pdc_cbo_leadership_position_id = '';
                    formValues.pdc_cbo_education_level_id = '';
                    const freshRoleNameOptions = await loadRoleNameOptions(formValues.pdc_entity_id);
                    setValues(prev => ({ ...prev, ...formValues }));
                    await renderTab('general', formValues, freshRoleNameOptions);
                },
                validations: { required: true },
                readOnly: isViewMode,
                columnSize: 12
            } : {},
            {
                name: 'pdc_role_name_id',
                type: 'select',
                label: 'Nome do Cargo',
                options: currentRoleNameOptions.map(row => ({ label: row.name, value: row.id })),
                handleChange: async (element, field, formValues) => {
                    const selected = currentRoleNameOptions.find(row => row.id === formValues.pdc_role_name_id);
                    formValues.cbo_display = selected ? selected.category_name : '';
                    setValues(prev => ({ ...prev, ...formValues }));
                    await renderTab('general', formValues, currentRoleNameOptions);
                },
                validations: { required: true },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'cbo_display',
                type: 'text',
                label: 'CBO',
                validations: { required: false },
                readOnly: true,
                columnSize: 12
            },
            {
                name: 'pdc_cbo_synonymous_id',
                type: 'select',
                label: 'Sinônimo CBO',
                isClearable: true,
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllCboSynonyms,
                    label: 'title',
                    value: 'id'
                },
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'pdc_cbo_role_class_id',
                type: 'select',
                label: 'Classe',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllRoleClasses,
                    label: 'name',
                    value: 'id',
                    params: { filter: { pdc_entity_id: entityId } }
                },
                handleChange: async (element, field, formValues) => {
                    formValues.pdc_cbo_role_classification_id = '';
                    setValues(prev => ({ ...prev, ...formValues }));
                    await renderTab('general', formValues, currentRoleNameOptions);
                },
                validations: { required: true },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'pdc_cbo_role_classification_id',
                type: 'select',
                label: 'Classificação',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllRoleClassifications,
                    label: 'code',
                    value: 'id',
                    params: {
                        filter: {
                            pdc_entity_id: entityId,
                            pdc_cbo_role_class_id: currentValues.pdc_cbo_role_class_id
                        }
                    }
                },
                handleChange: syncValues,
                validations: { required: true },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'pdc_cbo_sector_id',
                type: 'select',
                label: 'Setor',
                isClearable: true,
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllSectors,
                    label: 'name',
                    value: 'id',
                    params: { filter: { pdc_entity_id: entityId } }
                },
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'pdc_cbo_leadership_position_id',
                type: 'select',
                label: 'Liderança Imediata',
                isClearable: true,
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllLeadershipPositions,
                    label: 'name',
                    value: 'id',
                    params: { filter: { pdc_entity_id: entityId } }
                },
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'pdc_cbo_education_level_id',
                type: 'select',
                label: 'Escolaridade',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllEducationLevels,
                    label: 'name',
                    value: 'id',
                    params: { filter: { pdc_entity_id: entityId } }
                },
                handleChange: syncValues,
                validations: { required: true },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'education_level_detail',
                type: 'textarea',
                label: 'Detalhe da Escolaridade',
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'experience_time',
                type: 'text',
                label: 'Tempo de Experiência',
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'no_age_range',
                type: 'checkbox',
                label: 'Faixa Etária: Não se aplica',
                handleChange: async (element, field, formValues) => {
                    if (formValues.no_age_range === '1') {
                        formValues.min_age = '';
                        formValues.max_age = '';
                    }
                    setValues(prev => ({ ...prev, ...formValues }));
                    await renderTab('general', formValues, currentRoleNameOptions);
                },
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'min_age',
                type: 'integer',
                label: 'Idade mínima',
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode || currentValues.no_age_range === '1',
                columnSize: 6
            },
            {
                name: 'max_age',
                type: 'integer',
                label: 'Idade máxima',
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode || currentValues.no_age_range === '1',
                columnSize: 6
            },
            {
                name: 'professional_qualifications_items',
                type: 'multiinput',
                label: 'Qualificação Profissional',
                sortBy: '',
                showDeleteButton: true,
                form: [
                    {
                        name: 'text',
                        type: 'textarea',
                        label: 'Item',
                        rows: 3,
                        maxLength: 2000,
                        validations: { required: false }
                    }
                ],
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'attributions_items',
                type: 'multiinput',
                label: 'Atribuições',
                sortBy: '',
                showDeleteButton: true,
                form: [
                    {
                        name: 'text',
                        type: 'textarea',
                        label: 'Item',
                        rows: 3,
                        maxLength: 2000,
                        validations: { required: false }
                    }
                ],
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'status',
                type: 'radioGroup',
                handleChange: syncValues,
                options: [
                    { label: 'Ativo', value: '1', selected: true },
                    { label: 'Inativo', value: '0' }
                ],
                label: 'Status',
                validations: { required: true },
                readOnly: isViewMode,
                columnSize: 12
            }
        ];
    };

    const getCompetenciesFields = (currentValues) => {
        const entityId = !!currentValues.pdc_entity_id ? currentValues.pdc_entity_id : (!!profileFilter.pdc_entity_id ? profileFilter.pdc_entity_id : null);

        return [
            {
                name: 'technical_competency_ids',
                type: 'multiselect',
                label: 'Competências Técnicas',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllTechnicalCompetencies,
                    label: 'name',
                    value: 'id',
                    params: { filter: { pdc_entity_id: entityId } }
                },
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'behavioral_competency_ids',
                type: 'multiselect',
                label: 'Competências Comportamentais',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllBehavioralCompetencies,
                    label: 'name',
                    value: 'id',
                    params: { filter: { pdc_entity_id: entityId } }
                },
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            }
        ];
    };

    const getTrainingsFields = (currentValues) => {
        const entityId = !!currentValues.pdc_entity_id ? currentValues.pdc_entity_id : (!!profileFilter.pdc_entity_id ? profileFilter.pdc_entity_id : null);

        return [
            {
                name: 'training_ids',
                type: 'multiselect',
                label: 'Treinamentos Exigidos',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllTrainings,
                    label: 'name',
                    value: 'id',
                    params: { filter: { pdc_entity_id: entityId } }
                },
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            },
            {
                name: 'system_access_ids',
                type: 'multiselect',
                label: 'Acessos ao Sistema',
                options: [],
                optionsEndpoint: {
                    endpoint: paths.endpointGetAllSystemAccesses,
                    label: 'name',
                    value: 'id',
                    params: { filter: { pdc_entity_id: entityId } }
                },
                handleChange: syncValues,
                validations: { required: false },
                readOnly: isViewMode,
                columnSize: 12
            }
        ];
    };

    const renderTab = async (tabName, currentValues, roleNameOptionsList) => {
        const fields = tabName === 'general'
            ? getGeneralFields(currentValues, roleNameOptionsList)
            : tabName === 'competencies'
                ? getCompetenciesFields(currentValues)
                : getTrainingsFields(currentValues);

        const form = {
            validateOnChange: true,
            showSubmitButton: false,
            showGoBackButton: false,
            readOnly: isViewMode,
            hiddenValues: { ...hiddenValues },
            fields,
            values: currentValues
        };

        setFormContent(<></>);
        await sleep(1);
        setFormContent(<CustomForm form={form} />);
    };

    const switchTab = async (tabName) => {
        if (tabName === activeTab) return;
        setActiveTab(tabName);
        await renderTab(tabName, values);
    };

    useEffect(() => {
        const load = async () => {
            if (profileFilter.pdc_project_ids && basePath !== paths.pathView) {
                navigate(paths.urlGoBack);
                return;
            }

            let loadedValues = {
                status: '1',
                no_age_range: '0',
                ...hiddenValues
            };

            if (id) {
                const response = await Fetch.post(paths.endpointGetById, { id: id });
                loadedValues = {
                    ...response,
                    professional_qualifications_items: (response.professional_qualifications || []).map(text => ({ id: uuidv4(), text })),
                    attributions_items: (response.attributions || []).map(text => ({ id: uuidv4(), text })),
                    cbo_display: response.cbo
                };
            }

            const entityId = loadedValues.pdc_entity_id || profileFilter.pdc_entity_id;
            const freshRoleNameOptions = await loadRoleNameOptions(entityId);

            setValues(loadedValues);
            setIsLoading(false);
            await renderTab('general', loadedValues, freshRoleNameOptions);
        };
        load();
    }, []);

    const handleSave = async () => {
        const requiredFields = {
            pdc_entity_id: 'Entidade',
            pdc_role_name_id: 'Nome do Cargo',
            pdc_cbo_role_class_id: 'Classe',
            pdc_cbo_role_classification_id: 'Classificação',
            pdc_cbo_education_level_id: 'Escolaridade'
        };

        const entityValue = values.pdc_entity_id || profileFilter.pdc_entity_id;
        const missing = Object.keys(requiredFields).filter(key => {
            if (key === 'pdc_entity_id') return !entityValue;
            return !values[key];
        });

        if (missing.length > 0) {
            Toast.error(`Preencha os campos obrigatórios: ${missing.map(key => requiredFields[key]).join(', ')}`);
            setActiveTab('general');
            await renderTab('general', values);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...values,
                pdc_entity_id: entityValue,
                professional_qualifications: (values.professional_qualifications_items || []).map(item => (item.text || '').trim()).filter(text => text !== ''),
                attributions: (values.attributions_items || []).map(item => (item.text || '').trim()).filter(text => text !== '')
            };
            delete payload.professional_qualifications_items;
            delete payload.attributions_items;
            delete payload.cbo_display;
            delete payload.cbo;

            const response = await Fetch.put(paths.endpointSave, payload);

            if (response && response.status === 0) {
                Toast.error(response.message || 'Não foi possível salvar o registro.');
                setSaving(false);
                return;
            }

            Toast.success('Cargo salvo com sucesso');
            navigate(paths.urlGoBack);
        } catch (e) {
            Toast.error(e);
        }
        setSaving(false);
    };

    if (isLoading) {
        return <div>Carregando...</div>;
    }

    return (
        <section className="content" style={{ marginTop: '30px' }}>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card card-outline card-info">
                            <div className="card-header">
                                <h3 className="card-title">
                                    <i className="fa-solid fa-id-badge mr-1"></i>
                                    {id ? (isViewMode ? 'Visualizar Cargo' : 'Editar Cargo') : 'Novo Cargo'}
                                </h3>
                            </div>
                            <div className="card-body">
                                <Nav tabs>
                                    <NavItem>
                                        <NavLink
                                            style={{ cursor: 'pointer' }}
                                            className={classnames({ active: activeTab === 'general' })}
                                            onClick={() => switchTab('general')}
                                        >
                                            <i className="fas fa-clipboard mr-1"></i> Informações Gerais
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            style={{ cursor: 'pointer' }}
                                            className={classnames({ active: activeTab === 'competencies' })}
                                            onClick={() => switchTab('competencies')}
                                        >
                                            <i className="fas fa-cog mr-1"></i> Competências
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            style={{ cursor: 'pointer' }}
                                            className={classnames({ active: activeTab === 'trainingsAndAccess' })}
                                            onClick={() => switchTab('trainingsAndAccess')}
                                        >
                                            <i className="fas fa-key mr-1"></i> Treinamentos e Acessos
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <TabContent activeTab={activeTab} className="bg-white">
                                    <TabPane tabId={activeTab}>
                                        {formContent}
                                    </TabPane>
                                </TabContent>
                                <div style={{ marginTop: '20px', textAlign: 'right' }}>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        style={{ marginRight: '10px' }}
                                        onClick={() => navigate(paths.urlGoBack)}
                                    >
                                        {isViewMode ? 'Voltar' : 'Cancelar'}
                                    </button>
                                    {!isViewMode && (
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={saving}
                                            onClick={handleSave}
                                        >
                                            Salvar Cargo
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
export default Form;
